/**
 * 工具集面板
 * 左侧树形菜单 + 右侧操作页面
 */
import { useState } from 'react';
import {
  XMarkIcon,
  WrenchScrewdriverIcon,
  ScaleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import BidComparison from './BidComparison';
import BidReview from './BidReview';
import { useAuth } from '../../hooks/useAuth';

export type ToolKey = 'review' | 'comparison';

interface ToolDef {
  key: ToolKey;
  name: string;
  icon: React.ReactNode;
  feature: string; // 需要的授权功能（点号层级）
  render: (refreshToken: number) => React.ReactNode;
}

const TOOLS: ToolDef[] = [
  {
    key: 'review',
    name: '标书评审',
    icon: <ShieldExclamationIcon className="w-5 h-5" />,
    feature: 'tools.review',
    render: (refreshToken) => <BidReview refreshToken={refreshToken} />,
  },
  {
    key: 'comparison',
    name: '文档相似度',
    icon: <ScaleIcon className="w-5 h-5" />,
    feature: 'tools.compare',
    render: () => <BidComparison />,
  },
];

interface ToolsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ToolsPanel({ isOpen, onClose }: ToolsPanelProps) {
  const { license } = useAuth();
  const [activeTool, setActiveTool] = useState<ToolKey>('review');
  // 每次点击左侧工具时递增，用于触发目标工具重新拉取最新数据（如评审历史数量）
  const [refreshToken, setRefreshToken] = useState(0);

  // 授权判断（点号层级 + 祖先覆盖）：features 空=不限制
  const feats = license?.features;
  const hasFeat = (f: string): boolean => {
    if (!feats || feats.length === 0) return true;
    if (feats.includes(f)) return true;
    const parts = f.split('.');
    for (let i = 1; i < parts.length; i++) {
      if (feats.includes(parts.slice(0, i).join('.'))) return true;
    }
    return false;
  };
  const toolAllowed = (key: ToolKey): boolean => {
    const t = TOOLS.find((x) => x.key === key);
    return t ? hasFeat(t.feature) : false;
  };

  if (!isOpen) return null;

  const currentTool = TOOLS.find((t) => t.key === activeTool) || TOOLS[0];
  const currentAllowed = hasFeat(currentTool.feature);

  const handleSelectTool = (key: ToolKey) => {
    if (!toolAllowed(key)) return;
    setActiveTool(key);
    setRefreshToken((t) => t + 1);
  };

  return (
    <div className="fixed inset-0 z-[100] flex">
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* 工具面板 */}
      <div className="relative z-10 flex w-full max-w-6xl mx-auto my-6 bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* 左侧树形菜单 */}
        <div className="w-56 flex-shrink-0 border-r border-gray-200 bg-gray-50">
          {/* 菜单头部 */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <WrenchScrewdriverIcon className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-semibold text-gray-800">工具集</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* 工具列表 */}
          <nav className="py-2">
            {TOOLS.map((tool) => {
              const ok = hasFeat(tool.feature);
              return (
                <button
                  key={tool.key}
                  onClick={() => handleSelectTool(tool.key)}
                  disabled={!ok}
                  title={ok ? tool.name : `未授权「${tool.name}」，请联系管理员`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    !ok
                      ? 'text-gray-300 cursor-not-allowed border-l-2 border-transparent'
                      : activeTool === tool.key
                      ? 'bg-white text-blue-700 font-medium border-l-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-l-2 border-transparent'
                  }`}
                >
                  <span className={ok ? (activeTool === tool.key ? 'text-blue-600' : 'text-gray-400') : 'text-gray-300'}>
                    {tool.icon}
                  </span>
                  {tool.name}
                  {!ok && <span className="ml-auto text-xs text-red-400">未授权</span>}
                </button>
              );
            })}
          </nav>

          {/* 底部提示 */}
          <div className="absolute bottom-0 left-0 w-56 p-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-400 leading-relaxed">
              更多工具开发中...
            </p>
          </div>
        </div>

        {/* 右侧操作页面 */}
        <div className="flex-1 p-6 overflow-y-auto bg-white" style={{ maxHeight: 'calc(100vh - 48px)' }}>
          {currentAllowed ? (
            currentTool.render(refreshToken)
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <ShieldExclamationIcon className="w-10 h-10 mb-3" />
              <p className="text-sm">未授权「{currentTool.name}」功能</p>
              <p className="text-xs mt-1">请联系管理员在「系统授权」中导入对应授权</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
